import React, { useEffect, useRef } from 'react';
import { matchPath } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Box, Stack } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { maxWidths } from '@99designs/emu/settings';
import { Desktop } from '@99designs/spa-common';
import { useOnboarder } from '../Onboarder/OnboarderContext';
import {
    ControlsClearanceHeight,
    OnboarderControls,
} from '../Onboarder/OnboarderControls/OnboarderControls';
import { useOnboarderNav } from '../Onboarder/useOnboarderNav';
import { useRouteTransition } from '../RouteTransitionProvider';
import { StepHeader } from './StepHeader';
import { StepSummaryNav } from './StepSummaryNav';
import { createStepSummaries } from './createStepSummaries';
import type { Config } from '../Onboarder/Config';

interface Props {
    children: React.ReactNode;
}

export type animationDirection = 'forward' | 'back';

interface AnimationProps {
    state: string;
    delay?: string;
    animationDirection: animationDirection;
}

export const BodyAnimation = styled.div<AnimationProps>`
    transition: 0.5s;
    transition-delay: ${(props) => props.delay || '0s'};
    transition-timing-function: ease-in-out;
    ${({ state, animationDirection }) => {
        switch (state) {
            case 'from':
                return 'transform: translateY(40px); opacity: 0;';
            case 'enter':
                return 'transform: translateY(0); opacity: 1;';
            case 'leave':
                // eslint-disable-next-line eqeqeq
                if (animationDirection == 'back') {
                    return 'transform: translateY(40px); opacity: 0;';
                }

                return 'transform: translateY(-40px); opacity: 0;';
            default:
                return 'transform: translateY(20px); opacity: 0;';
        }
    }}
`;

interface HeaderAnimationProps extends AnimationProps {
    showSkippedAnimation: boolean;
}

export const HeaderAnimation = styled.div<HeaderAnimationProps>`
    transition: all 0.5s ease-in-out;
    transition-delay: ${(props) => props.delay || '0s'};
    h1 {
        transition: all 0.5s ease-in-out;
    }

    ${({ state, animationDirection, showSkippedAnimation }) => {
        switch (state) {
            case 'from':
                return 'transform: translateY(40px); opacity: 0;';
            case 'enter':
                return 'transform: translateY(0); opacity: 1;';
            case 'leave':
                if (animationDirection === 'back') {
                    return 'transform: translateY(40px); opacity: 0;';
                }
                if (showSkippedAnimation) {
                    return 'transform: translateY(-20px); opacity: 0;';
                }

                return `
                    transform: translateY(-40px);
                    opacity: 0;
                    ${mediaQuery.minimum('normal')} {
                        h1 {
                            font-size: 25px;
                            letter-spacing: 0.12px:
                        }
                        transform: translateY(-45px);
                        opacity: 0.7;
                    }

                `;
            default:
                return 'transform: translateY(0); opacity: 1;';
        }
    }}
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getStepIndex(location: string | null, config: Config<any>): number {
    if (!location) {
        return -1;
    }
    const match = matchPath(
        {
            path: `${config.basePath}/:step/:name`,
            end: true,
            caseSensitive: true,
        },
        location
    );
    if (match && match.params.step) {
        const step = parseInt(match.params.step, 10);
        if (step > 0) {
            return step - 1;
        }
    }
    return -1;
}

const BoxWithCustomMarginForFixedControls = styled(Box)<{ centeredVertically: boolean }>`
    margin-bottom: ${ControlsClearanceHeight};
    ${({ centeredVertically }) =>
        centeredVertically &&
        css`
            display: flex;
            align-items: center;
        `}
`;

export function StepLayout({ children }: Props) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { config, data } = useOnboarder<any>();
    const onboarderNav = useOnboarderNav();

    const { transitionState, previousLocation, nextLocation, enableTransition } =
        useRouteTransition();

    useEffect(() => {
        return enableTransition(500);
    });

    const previousStepIndex = getStepIndex(previousLocation, config);
    const nextStepIndex = getStepIndex(nextLocation, config);
    const currentStepIndex = onboarderNav.currentStepIndex();

    const isNavigatingBack = nextStepIndex < currentStepIndex;
    const summaries = createStepSummaries(config, data);

    const showSkippedAnimation = summaries[currentStepIndex]
        ? summaries[currentStepIndex].hideIfEmpty
        : false;

    const headerRef = useRef<HTMLDivElement>(null);
    const showStepSummary = summaries.length > 0;

    return (
        <>
            <BoxWithCustomMarginForFixedControls
                flex="1"
                data-testid={`onboarder-step-${currentStepIndex + 1}`}
                maxWidth={`${maxWidths.content.widescreen}px`}
                width="100%"
                margin="auto"
                padding="1.5bu"
                centeredVertically={!showStepSummary}
                role="main"
            >
                <Box maxWidth="100%" width="100%" paddingTop={['1.5bu', '8bu', 'none', 'none']}>
                    <Desktop>
                        {showStepSummary && (
                            <StepSummaryNav
                                key={`summary-${currentStepIndex}`}
                                summaries={summaries}
                                currentStepIndex={currentStepIndex}
                                nextStepIndex={nextStepIndex}
                                previousStepIndex={previousStepIndex}
                                transitionState={transitionState}
                            />
                        )}
                    </Desktop>
                    <Stack space={['2bu', '4bu']}>
                        <HeaderAnimation
                            state={transitionState}
                            animationDirection={isNavigatingBack ? 'back' : 'forward'}
                            key={`header-${currentStepIndex}`}
                            showSkippedAnimation={showSkippedAnimation}
                        >
                            <div ref={headerRef}>
                                <StepHeader />
                            </div>
                        </HeaderAnimation>
                        <BodyAnimation
                            state={transitionState}
                            animationDirection={isNavigatingBack ? 'back' : 'forward'}
                            delay="0.1s"
                            key={`content-${currentStepIndex}`}
                        >
                            {children}
                        </BodyAnimation>
                    </Stack>
                </Box>
            </BoxWithCustomMarginForFixedControls>
            <OnboarderControls headerRef={headerRef} />
        </>
    );
}
